import type { AuthSession, User } from '@/api/types'

export const AUTH_STORAGE_KEY = 'oo.auth'

export interface PersistedAuth {
  access_token: string
  refresh_token: string
  user: User
}

/**
 * La forme est vérifiée **champ par champ** sur ce dont l'application se sert
 * avant la revalidation : `timezone` définit la journée de ses saisies, et un
 * blob écrit avant le lot 8 ne le porte pas. Le type `User` dit obligatoire ce
 * que le serveur rend toujours ; c'est donc ici qu'un contenu plus vieux que le
 * type doit être écarté, faute de quoi la session mentirait au type.
 */
function isPersistedAuth(value: unknown): value is PersistedAuth {
  if (typeof value !== 'object' || value === null) return false
  const candidate: Record<string, unknown> = value as Record<string, unknown>
  const user = candidate.user
  if (typeof user !== 'object' || user === null) return false
  const fields: Record<string, unknown> = user as Record<string, unknown>
  return (
    typeof candidate.access_token === 'string' &&
    typeof candidate.refresh_token === 'string' &&
    typeof fields.id === 'string' &&
    typeof fields.timezone === 'string' &&
    (fields.weekly_capacity_minutes === null || typeof fields.weekly_capacity_minutes === 'number')
  )
}

/**
 * 1.90.0 : une session écrite avant les rôles d'équipe ne les porte pas. Elle
 * reste valide — `GET /auth/me` les rend aussitôt — et vaut `[]` d'ici là,
 * plutôt que de déconnecter tout le monde à la mise à jour.
 */
function withRoles(session: PersistedAuth): PersistedAuth {
  const roles: unknown = (session.user as Partial<User>).roles
  return Array.isArray(roles) ? session : { ...session, user: { ...session.user, roles: [] } }
}

/** Relit la session depuis `localStorage`, en ignorant tout contenu corrompu. */
export function readPersistedAuth(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isPersistedAuth(parsed) ? withRoles(parsed) : null
  } catch {
    return null
  }
}

export function writePersistedAuth(session: PersistedAuth): void {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session))
  } catch {
    /* Quota ou mode privé : la session reste valide en mémoire. */
  }
}

export function clearPersistedAuth(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY)
  } catch {
    /* Rien à faire : l'état mémoire fait foi. */
  }
}

export function toPersisted(session: AuthSession): PersistedAuth {
  return {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    user: session.user,
  }
}
